'use client';

import {
  Stepper,
  StepperIndicator,
  StepperItem,
  StepperSeparator,
  StepperTitle,
} from '@/components/ui/stepper';
import { Icons } from '@/components/icons';
import { useCallback, useMemo, useState } from 'react';
import {
  type ContextCustomFieldInclusion,
  type ErrandSubTypeContext,
  isAllowedForAnonymous,
  isRequiredForAnonymous,
  resolveErrandTypeConfig,
  useGetErrandContext,
} from '../../[id]/hooks/use-get-errand-context';
import { uploadErrandFiles } from '../../[id]/utils/upload-files';
import { useCreateErrand } from '../hooks/use-create-errand';
import type { CreateErrandFormValues, CreateErrandPayload } from '../types';
import { ErrandDetailsStep } from './errand-details-step';
import { ErrandTypeStep } from './errand-type-step';

interface CreateErrandFormProps {
  readonly customerId: string;
}

interface StepDef {
  readonly id: string;
  readonly label: string;
}

export function CreateErrandForm({ customerId }: CreateErrandFormProps) {
  const {
    data: contextData,
    isLoading,
    isError,
  } = useGetErrandContext({ customerId });
  const createErrand = useCreateErrand({ customerId });

  const [activeStep, setActiveStep] = useState(0);
  const [errandTypeId, setErrandTypeId] = useState<string | null>(null);
  const [subTypeId, setSubTypeId] = useState<string | null>(null);
  const [addressId, setAddressId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [createdErrandNumber, setCreatedErrandNumber] = useState<string | null>(
    null
  );

  const resolvedConfig = useMemo(
    () => resolveErrandTypeConfig(contextData, errandTypeId, subTypeId),
    [contextData, errandTypeId, subTypeId]
  );

  const selectedErrandType = useMemo(
    () => contextData?.errand_types.find((type) => type.id === errandTypeId),
    [contextData, errandTypeId]
  );

  const isSubTypeRequired = useMemo(
    () =>
      selectedErrandType != null &&
      isRequiredForAnonymous(selectedErrandType.sub_type_configuration),
    [selectedErrandType]
  );

  const availableSubTypes = useMemo<ErrandSubTypeContext[]>(() => {
    if (
      !contextData ||
      !errandTypeId ||
      !selectedErrandType ||
      !isAllowedForAnonymous(selectedErrandType.sub_type_configuration)
    ) {
      return [];
    }

    return contextData.errand_sub_types
      .filter((subType) => subType.errand_type.id === errandTypeId)
      .toSorted((a, b) => a.name.localeCompare(b.name, 'sv'));
  }, [contextData, errandTypeId, selectedErrandType]);

  const customFieldInclusions = useMemo<ContextCustomFieldInclusion[]>(() => {
    if (!contextData || !errandTypeId) return [];

    const byFieldType = new Map<string, ContextCustomFieldInclusion>();

    for (const inc of contextData.custom_field_inclusions ?? []) {
      if (inc.general_errand_type === errandTypeId) {
        byFieldType.set(inc.field_type.id, inc);
      }
    }

    if (subTypeId) {
      for (const inc of contextData.custom_field_inclusions ?? []) {
        if (inc.sub_errand_type === subTypeId) {
          byFieldType.set(inc.field_type.id, inc);
        }
      }
    }

    return Array.from(byFieldType.values()).toSorted(
      (a, b) => a.order - b.order
    );
  }, [contextData, errandTypeId, subTypeId]);

  const showAddressStep = useMemo(
    () =>
      resolvedConfig != null &&
      isAllowedForAnonymous(resolvedConfig.address_configuration),
    [resolvedConfig]
  );

  const isAddressRequired = useMemo(
    () =>
      resolvedConfig != null &&
      isRequiredForAnonymous(resolvedConfig.address_configuration),
    [resolvedConfig]
  );

  const steps = useMemo<StepDef[]>(
    () => [
      { id: 'type', label: 'Ärendetyp' },
      { id: 'details', label: 'Detaljer' },
    ],
    []
  );

  const currentStepId = steps[activeStep]?.id ?? 'type';

  const handleSelectType = useCallback(
    (typeId: string) => {
      setErrandTypeId(typeId);
      setSubTypeId(null);
      // Reset address if type changes
      setAddressId(null);

      const selectedType = contextData?.errand_types.find(
        (type) => type.id === typeId
      );
      const hasAvailableSubTypes =
        selectedType != null &&
        isAllowedForAnonymous(selectedType.sub_type_configuration) &&
        contextData?.errand_sub_types.some(
          (subType) => subType.errand_type.id === typeId
        );

      setActiveStep(hasAvailableSubTypes ? 0 : 1);
    },
    [contextData]
  );

  // Auto-select the errand type when there is only one to choose from
  if (errandTypeId == null && contextData?.errand_types.length === 1) {
    handleSelectType(contextData.errand_types[0].id);
  }

  const handleSelectSubType = useCallback(
    (selectedSubTypeId: string | null) => {
      setSubTypeId(selectedSubTypeId);
      setActiveStep(1);
    },
    []
  );

  const handleBack = useCallback(() => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  }, []);

  const handleSubmit = useCallback(
    async (values: CreateErrandFormValues) => {
      if (!errandTypeId) return;
      setSubmitError(null);

      if (isAddressRequired && !addressId) {
        setSubmitError({ address: 'Adress krävs.' });
        return;
      }

      if (isSubTypeRequired && availableSubTypes.length > 0 && !subTypeId) {
        setSubmitError({ errand_sub_type: 'Undertyp krävs.' });
        return;
      }

      const hasReporterData =
        values.reporter_first_name ||
        values.reporter_last_name ||
        values.reporter_email ||
        values.reporter_phone_number ||
        values.reporter_ssn ||
        values.reporter_org_no;

      const newFiles = values.errand_files.filter(
        (f) => f.file instanceof File && f.md5_hash && f.processed_name
      );

      const customFieldValues = customFieldInclusions.flatMap((inclusion) => {
        const fieldTypeId = inclusion.field_type.id;
        const value = values.custom_field_values[fieldTypeId] ?? '';
        return value.trim() === ''
          ? []
          : [{ field_type: { id: fieldTypeId }, value }];
      });

      const payload: CreateErrandPayload = {
        errand_type: { id: errandTypeId },
        ...(subTypeId ? { errand_sub_type: { id: subTypeId } } : {}),
        errand: {
          title: values.title,
          ...(values.description ? { description: values.description } : {}),
          ...(values.has_pets != null ? { has_pets: values.has_pets } : {}),
          ...(values.object_access != null
            ? { object_access: values.object_access }
            : {}),
        },
        ...(addressId ? { address: { id: addressId } } : {}),
        ...(hasReporterData
          ? {
              reporter: {
                ...(values.reporter_first_name
                  ? { first_name: values.reporter_first_name }
                  : {}),
                ...(values.reporter_last_name
                  ? { last_name: values.reporter_last_name }
                  : {}),
                ...(values.reporter_email
                  ? { email: values.reporter_email }
                  : {}),
                ...(values.reporter_phone_number
                  ? { phone_number: values.reporter_phone_number }
                  : {}),
                ...(values.reporter_ssn ? { ssn: values.reporter_ssn } : {}),
                ...(values.reporter_org_no
                  ? { org_no: values.reporter_org_no }
                  : {}),
              },
            }
          : {}),
        ...(newFiles.length > 0
          ? {
              errand_files: newFiles.map((f) => ({
                file_obj: {
                  name: f.processed_name!,
                  md5_hash: f.md5_hash!,
                },
              })),
            }
          : {}),
        ...(customFieldValues.length > 0
          ? { custom_field_values: customFieldValues }
          : {}),
      };

      try {
        const response = await createErrand.mutateAsync(payload);

        // Upload files to S3 if there are any
        try {
          if (newFiles.length > 0 && response.errand_files.length > 0) {
            await uploadErrandFiles(
              newFiles.map((f) => ({
                id: undefined,
                file: f.file,
                preview_url: undefined,
                md5_hash: f.md5_hash,
                processed_name: f.processed_name,
              })),
              response.errand_files.map((rf) => ({
                file_obj: {
                  name: rf.file_obj.name,
                  get: rf.file_obj.get,
                  post: rf.file_obj.post,
                },
                id: rf.id,
              }))
            );
          }
        } catch {
          // Errand created but file upload failed — still show success with warning
          setCreatedErrandNumber(response.id);
          setSubmitError({
            message:
              'Ärendet skapades men filuppladdningen misslyckades. Filerna kan bifogas senare.',
          });
          setIsSuccess(true);
          return;
        }

        setCreatedErrandNumber(response.id);
        setIsSuccess(true);
      } catch (err) {
        if (err && typeof err === 'object' && !('message' in err)) {
          setSubmitError(err as Record<string, unknown>);
        } else {
          setSubmitError({ message: 'Ett oväntat fel inträffade.' });
        }
      }
    },
    [
      errandTypeId,
      subTypeId,
      addressId,
      isAddressRequired,
      isSubTypeRequired,
      availableSubTypes.length,
      customFieldInclusions,
      createErrand,
    ]
  );

  if (isSuccess) {
    return (
      <div className='mx-auto max-w-lg px-4 py-16'>
        <div className='flex flex-col items-center gap-4 text-center'>
          <Icons.success className='size-16 text-green-500' />
          <h1 className='text-2xl font-bold'>Tack för din anmälan!</h1>
          <p className='text-muted-foreground'>
            Ditt ärende har skapats. Du kommer att få en bekräftelse via e-post
            eller SMS med en länk där du kan följa ärendets status.
          </p>
          {submitError && 'message' in submitError && (
            <p className='text-sm text-amber-600'>
              {String(submitError.message)}
            </p>
          )}
          {createdErrandNumber && (
            <p className='text-sm text-muted-foreground'>
              Referens-ID:{' '}
              <span className='font-mono font-medium text-foreground'>
                {createdErrandNumber}
              </span>
            </p>
          )}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='flex min-h-[50vh] items-center justify-center'>
        <Icons.loader className='size-8 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (isError || !contextData) {
    return (
      <div className='flex min-h-[50vh] flex-col items-center justify-center gap-4'>
        <p className='text-muted-foreground'>
          Kunde inte ladda formuläret. Försök igen senare.
        </p>
      </div>
    );
  }

  return (
    <div className='mx-auto max-w-2xl px-4 py-8'>
      <h1 className='mb-6 text-2xl font-bold'>Skapa ärende</h1>

      {/* Stepper indicator */}
      <Stepper value={activeStep + 1} className='mb-8'>
        {steps.map((step, index) => (
          <StepperItem key={step.id} step={index + 1} className='flex-1 gap-2'>
            <StepperIndicator className='data-[state=active]:bg-foreground data-[state=completed]:bg-foreground' />
            <StepperTitle className='hidden sm:block'>
              {step.label}
            </StepperTitle>
            {index < steps.length - 1 && (
              <StepperSeparator className='group-data-[state=completed]/step:bg-foreground' />
            )}
          </StepperItem>
        ))}
      </Stepper>

      {/* Step content */}
      {currentStepId === 'type' && (
        <ErrandTypeStep
          errandTypes={contextData.errand_types}
          subTypes={availableSubTypes}
          selectedTypeId={errandTypeId}
          selectedSubTypeId={subTypeId}
          isSubTypeRequired={isSubTypeRequired}
          onSelect={handleSelectType}
          onSelectSubType={handleSelectSubType}
        />
      )}

      {/* Keep details step mounted but hidden to preserve form state */}
      {resolvedConfig && contextData && (
        <div className={currentStepId === 'details' ? undefined : 'hidden'}>
          <ErrandDetailsStep
            config={resolvedConfig}
            contextData={contextData}
            addresses={contextData.addresses}
            selectedAddressId={addressId}
            showAddress={showAddressStep}
            isAddressRequired={isAddressRequired}
            customFieldInclusions={customFieldInclusions}
            onSelectAddress={setAddressId}
            isSubmitting={createErrand.isPending}
            submitError={submitError}
            onSubmit={handleSubmit}
            onBack={handleBack}
          />
        </div>
      )}
    </div>
  );
}
